import { useState, useEffect, useRef, useReducer, useContext } from 'react';
import { NativeModules, Platform } from 'react-native';
import RealtimeKitClient, {
  RealtimeKitClientOptions,
} from '@cloudflare/realtimekit';
import { RealtimeKitContext } from './ReactContext';
import BackgroundTimer from './BackgroundHandler';
import { webRTCModuleEmitter } from './AudioSampleHandler';
import { deviceChangeListener } from './LocalMediaUtils';
import { broadcastEmitter } from './LocalMediaHandler';

/**
 * Hook which manages a RealtimeKitClient instance
 */
export const useRealtimeKitClient = (): [
  RealtimeKitClient | undefined,
  (options: RealtimeKitClientOptions) => Promise<RealtimeKitClient>
] => {
  const [client, setClient] = useState<RealtimeKitClient>();
  const initClient = async (
    options: RealtimeKitClientOptions
  ): Promise<RealtimeKitClient> => {
    if (client) cleanUpEvents();
    const cli = await RealtimeKitClient.init(options);
    setClient(cli);
    return cli;
  };
  return [client, initClient];
};

/**
 * Utility function which determines whether to trigger update or not
 */
const shouldUpdate = <StateSlice,>(
  slice: StateSlice,
  newSlice: StateSlice
): boolean => {
  if (slice !== newSlice) {
    return true;
  }
  if (Array.isArray(slice) && Array.isArray(newSlice)) {
    if (slice.length !== newSlice.length) {
      return true;
    }
  }

  return false;
};

type StateSelector<T extends object, U> = (state: T) => U;

/**
 * @deprecated Use `useRealtimeKitSelector` instead.
 * Hook which extracts data from the RealtimeKitClient object
 */
export const useRTKSelector = <StateSlice,>(
  selector: StateSelector<RealtimeKitClient, StateSlice>
) => {
  return useRealtimeKitSelector(selector);
};

/**
 * Hook which extracts data from the RealtimeKitClient object
 */
export const useRealtimeKitSelector = <StateSlice,>(
  selector: StateSelector<RealtimeKitClient, StateSlice>
) => {
  const { meeting, updates } = useContext(RealtimeKitContext);
  if (!meeting)
    throw new Error(
      'useRealtimeKitSelector must be used within the RealtimeKitProvider'
    );
  const state = useRef(selector(meeting));
  const [, forceUpdate] = useReducer((c) => c + 1, 0) as [never, () => void];

  useEffect(() => {
    const currentState = meeting && selector(meeting);
    const listener = () => {
      const newStateSlice = meeting && selector(meeting);
      if (shouldUpdate(state.current, newStateSlice)) {
        state.current = newStateSlice;
        forceUpdate();
      }
    };
    if (
      currentState &&
      typeof currentState === 'object' &&
      'addListener' in currentState
    ) {
      (currentState as any).addListener('*', forceUpdate);
      return () => {
        (currentState as any).removeListener('*', forceUpdate);
      };
    }
    updates?.addListener(listener);
    return () => {
      updates?.removeListener(listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting, updates]);
  return state.current;
};

function cleanUpEvents() {
  const { RTKHelper } = NativeModules;

  webRTCModuleEmitter.removeAllListeners('audioSamples');
  deviceChangeListener.removeAllListeners('onAudioDeviceChanged');
  BackgroundTimer.stop();

  try {
    if (Platform.OS === 'android') {
      RTKHelper.isForegroundServiceRunning().then((val) => {
        if (val) {
          RTKHelper.stopService();
        }
      });
    } else {
      broadcastEmitter.removeAllListeners('iOS_BroadcastStarted');
      broadcastEmitter.removeAllListeners('iOS_BroadcastStopped');
    }
  } catch (_err) {}
}
export * from './ReactContext';
